import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'react-hot-toast';
import { motion, AnimatePresence } from 'framer-motion';
import {
    PlusIcon,
    TicketIcon,
    PencilSquareIcon,
    TrashIcon,
    XMarkIcon,
    CalendarIcon,
    BanknotesIcon,
    UserIcon,
    TagIcon
} from '@heroicons/react/24/outline';
import { API } from '../../config';

const AdminCoupons = () => {
    const [coupons, setCoupons] = useState([]);
    const [showAddForm, setShowAddForm] = useState(false);
    const [loading, setLoading] = useState(false);
    const [editingIndex, setEditingIndex] = useState(null);
    const [formData, setFormData] = useState({
        user: 'Admin',
        name: '',
        discountType: 'percentage',
        discountValue: '',
        expiryDate: '',
        usageLimit: '',
        status: 'active',
        minOrderValue: '',
    });

    useEffect(() => {
        fetchCoupons();
    }, []);

    const fetchCoupons = async () => {
        try {
            const token = localStorage.getItem('token');
            const response = await axios.get(`${API}/coupons`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            const mapped = (response.data || []).map(coupon => ({
                id: coupon.id,
                user: coupon.coupon_user,
                name: coupon.coupon_name,
                discountType: coupon.type,
                discountValue: coupon.discount,
                expiryDate: coupon.valid_to ? coupon.valid_to.split('T')[0] : '',
                usageLimit: coupon.usage_limit,
                status: coupon.status,
                minOrderValue: coupon.min_purchase,
            }));
            setCoupons(mapped);
        } catch (error) {
            setCoupons([]);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            const token = localStorage.getItem('token');
            if (editingIndex !== null) {
                const coupon = coupons[editingIndex];
                await axios.put(`${API}/coupons/${coupon.id}`, formData, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                toast.success('Coupon updated successfully!');
            } else {
                await axios.post(`${API}/coupons`, formData, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                toast.success('Coupon added successfully!');
            }
            resetForm();
            fetchCoupons();
        } catch (error) {
            toast.error('Failed to save coupon');
        } finally {
            setLoading(false);
        }
    };

    const resetForm = () => {
        setFormData({ user: 'Admin', name: '', discountType: 'percentage', discountValue: '', expiryDate: '', usageLimit: '', status: 'active', minOrderValue: '' });
        setShowAddForm(false);
        setEditingIndex(null);
    };

    const handleEdit = (index) => {
        const coupon = coupons[index];
        setFormData({
            user: coupon.user,
            name: coupon.name,
            discountType: coupon.discountType,
            discountValue: coupon.discountValue,
            expiryDate: coupon.expiryDate,
            usageLimit: coupon.usageLimit,
            status: coupon.status,
            minOrderValue: coupon.minOrderValue,
        });
        setEditingIndex(index);
        setShowAddForm(true);
    };

    const handleDelete = async (index) => {
        const coupon = coupons[index];
        if (!window.confirm('Delete this coupon?')) return;
        setLoading(true);
        try {
            const token = localStorage.getItem('token');
            await axios.delete(`${API}/coupons/${coupon.id}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            toast.success('Coupon deleted!');
            fetchCoupons();
        } catch (error) {
            toast.error('Failed to delete coupon');
        } finally {
            setLoading(false);
        }
    };

    return (
        <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-3xl shadow-sm p-8"
        >
            <div className="flex justify-between items-center mb-8">
                <div>
                    <h2 className="text-2xl font-bold text-gray-900">Coupons</h2>
                    <p className="text-gray-500 text-sm mt-1">Manage discounts and promotional codes</p>
                </div>
                <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => { resetForm(); setShowAddForm(true); }}
                    className="bg-gray-900 text-white px-5 py-2.5 rounded-xl hover:bg-black transition-all shadow-sm hover:shadow-md flex items-center font-medium text-sm"
                >
                    <PlusIcon className="h-4 w-4 mr-2" />
                    Add Coupon
                </motion.button>
            </div>

            <AnimatePresence>
                {showAddForm && (
                    <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        exit={{ opacity: 0, height: 0 }}
                        className="overflow-hidden mb-8"
                    >
                        <div className="bg-gray-50 rounded-3xl p-8 border border-gray-200">
                            <div className="flex justify-between items-center mb-6">
                                <h3 className="text-lg font-bold text-gray-900 flex items-center">
                                    {editingIndex !== null ? <PencilSquareIcon className="h-5 w-5 mr-2 text-gray-700" /> : <PlusIcon className="h-5 w-5 mr-2 text-gray-700" />}
                                    {editingIndex !== null ? 'Edit Coupon' : 'Add New Coupon'}
                                </h3>
                                <button
                                    onClick={resetForm}
                                    className="text-gray-400 hover:text-gray-600 transition-colors"
                                >
                                    <XMarkIcon className="h-5 w-5" />
                                </button>
                            </div>

                            <form onSubmit={handleSubmit} className="space-y-8">
                                <div className="grid md:grid-cols-2 gap-8">
                                    <div>
                                        <label className="block text-xs font-semibold text-gray-700 mb-1.5 uppercase tracking-wide flex items-center">
                                            <UserIcon className="h-4 w-4 mr-1.5" />
                                            Coupon User
                                        </label>
                                        <input
                                            type="text"
                                            value={formData.user}
                                            onChange={(e) => setFormData({ ...formData, user: e.target.value })}
                                            className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:ring-1 focus:ring-gray-900 focus:border-gray-900 bg-white text-sm"
                                            placeholder="User ID or Email"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-xs font-semibold text-gray-700 mb-1.5 uppercase tracking-wide flex items-center">
                                            <TicketIcon className="h-4 w-4 mr-1.5" />
                                            Coupon Name
                                        </label>
                                        <input
                                            type="text"
                                            value={formData.name}
                                            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                            className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:ring-1 focus:ring-gray-900 focus:border-gray-900 bg-white text-sm"
                                            placeholder="e.g. SUMMER2024"
                                        />
                                    </div>
                                </div>
                                <div className="grid md:grid-cols-3 gap-8">
                                    <div>
                                        <label className="block text-xs font-semibold text-gray-700 mb-1.5 uppercase tracking-wide flex items-center">
                                            <TagIcon className="h-4 w-4 mr-1.5" />
                                            Discount Type
                                        </label>
                                        <select
                                            value={formData.discountType}
                                            onChange={(e) => setFormData({ ...formData, discountType: e.target.value })}
                                            className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:ring-1 focus:ring-gray-900 focus:border-gray-900 bg-white text-sm"
                                        >
                                            <option value="percentage">Percentage (%)</option>
                                            <option value="fixed">Fixed Amount (₹)</option>
                                        </select>
                                    </div>
                                    <div>
                                        <label className="block text-xs font-semibold text-gray-700 mb-1.5 uppercase tracking-wide">Discount Value</label>
                                        <input
                                            type="number"
                                            min="0"
                                            value={formData.discountValue}
                                            onChange={(e) => setFormData({ ...formData, discountValue: e.target.value })}
                                            className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:ring-1 focus:ring-gray-900 focus:border-gray-900 bg-white text-sm"
                                            placeholder="e.g. 10"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-xs font-semibold text-gray-700 mb-1.5 uppercase tracking-wide flex items-center">
                                            <BanknotesIcon className="h-4 w-4 mr-1.5" />
                                            Min Order Value
                                        </label>
                                        <input
                                            type="number"
                                            min="0"
                                            value={formData.minOrderValue}
                                            onChange={e => setFormData({ ...formData, minOrderValue: e.target.value })}
                                            className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:ring-1 focus:ring-gray-900 focus:border-gray-900 bg-white text-sm"
                                            placeholder="e.g. 500"
                                        />
                                    </div>
                                </div>
                                <div className="grid md:grid-cols-3 gap-8">
                                    <div>
                                        <label className="block text-xs font-semibold text-gray-700 mb-1.5 uppercase tracking-wide flex items-center">
                                            <CalendarIcon className="h-4 w-4 mr-1.5" />
                                            Expiry Date
                                        </label>
                                        <input
                                            type="date"
                                            value={formData.expiryDate}
                                            onChange={(e) => setFormData({ ...formData, expiryDate: e.target.value })}
                                            className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:ring-1 focus:ring-gray-900 focus:border-gray-900 bg-white text-sm"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-xs font-semibold text-gray-700 mb-1.5 uppercase tracking-wide">Usage Limit</label>
                                        <input
                                            type="number"
                                            min="1"
                                            value={formData.usageLimit}
                                            onChange={(e) => setFormData({ ...formData, usageLimit: e.target.value })}
                                            className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:ring-1 focus:ring-gray-900 focus:border-gray-900 bg-white text-sm"
                                            placeholder="Max uses"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-xs font-semibold text-gray-700 mb-1.5 uppercase tracking-wide">Status</label>
                                        <select
                                            value={formData.status}
                                            onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                                            className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:ring-1 focus:ring-gray-900 focus:border-gray-900 bg-white text-sm"
                                        >
                                            <option value="active">Active</option>
                                            <option value="inactive">Inactive</option>
                                            <option value="expired">Expired</option>
                                        </select>
                                    </div>
                                </div>
                                <div className="flex space-x-3 pt-4 border-t border-gray-200">
                                    <motion.button
                                        whileHover={{ scale: 1.01 }}
                                        whileTap={{ scale: 0.99 }}
                                        type="submit"
                                        disabled={loading}
                                        className="bg-gray-900 text-white px-6 py-2.5 rounded-xl hover:bg-black transition-all shadow-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed flex-1 text-sm"
                                    >
                                        {loading ? 'Saving...' : (editingIndex !== null ? 'Update Coupon' : 'Add Coupon')}
                                    </motion.button>
                                    <motion.button
                                        whileHover={{ scale: 1.01 }}
                                        whileTap={{ scale: 0.99 }}
                                        type="button"
                                        onClick={resetForm}
                                        className="bg-white text-gray-700 border border-gray-300 px-6 py-2.5 rounded-xl hover:bg-gray-50 transition-all font-medium text-sm"
                                    >
                                        Cancel
                                    </motion.button>
                                </div>
                            </form>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* Coupons List */}
            <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
                <div className="overflow-x-auto custom-scrollbar">
                    <table className="min-w-full divide-y divide-gray-100">
                        <thead className="bg-gray-50">
                            <tr>
                                <th className="px-6 py-4 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Name</th>
                                <th className="px-6 py-4 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">User</th>
                                <th className="px-6 py-4 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Discount</th>
                                <th className="px-6 py-4 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Min Order</th>
                                <th className="px-6 py-4 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Expiry</th>
                                <th className="px-6 py-4 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Usage</th>
                                <th className="px-6 py-4 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Status</th>
                                <th className="px-6 py-4 text-right text-xs font-bold text-gray-500 uppercase tracking-wider">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-100">
                            {coupons.length === 0 ? (
                                <tr>
                                    <td colSpan="8" className="px-6 py-12 text-center text-gray-500">
                                        <div className="flex flex-col items-center justify-center">
                                            <TicketIcon className="h-12 w-12 text-gray-300 mb-3" />
                                            <p className="font-medium text-gray-900">No coupons found</p>
                                            <p className="text-sm text-gray-500 mt-1">Create your first coupon above</p>
                                        </div>
                                    </td>
                                </tr>
                            ) : (
                                coupons.map((coupon, idx) => (
                                    <motion.tr
                                        key={idx}
                                        initial={{ opacity: 0 }}
                                        animate={{ opacity: 1 }}
                                        transition={{ delay: idx * 0.05 }}
                                        className="hover:bg-gray-50 transition-colors group"
                                    >
                                        <td className="px-6 py-4 whitespace-nowrap font-medium text-gray-900">{coupon.name}</td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{coupon.user}</td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 font-medium">
                                            <span className="bg-gray-100 px-2 py-1 rounded-lg">
                                                {coupon.discountType === 'percentage' ? `${coupon.discountValue}%` : `₹${coupon.discountValue}`}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">₹{coupon.minOrderValue || '0'}</td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{coupon.expiryDate || 'N/A'}</td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{coupon.usageLimit || 'Unlimited'}</td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <span className={`inline-flex px-2.5 py-1 text-xs font-bold rounded-full ${coupon.status === 'active' ? 'bg-green-100 text-green-800' :
                                                coupon.status === 'expired' ? 'bg-red-100 text-red-800' :
                                                    'bg-gray-100 text-gray-800'
                                                }`}>
                                                {coupon.status.charAt(0).toUpperCase() + coupon.status.slice(1)}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                            <div className="flex justify-end space-x-2 opacity-0 group-hover:opacity-100 transition-opacity">
                                                <button
                                                    onClick={() => handleEdit(idx)}
                                                    className="text-gray-400 hover:text-gray-900 transition-colors p-1"
                                                    title="Edit"
                                                >
                                                    <PencilSquareIcon className="h-5 w-5" />
                                                </button>
                                                <button
                                                    onClick={() => handleDelete(idx)}
                                                    className="text-gray-400 hover:text-red-600 transition-colors p-1"
                                                    title="Delete"
                                                >
                                                    <TrashIcon className="h-5 w-5" />
                                                </button>
                                            </div>
                                        </td>
                                    </motion.tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        </motion.div>
    );
};

export default AdminCoupons;
